/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { ACCESS_TYPES, ContentRequest, ReferenceBooks, TVDBChannel, useExecPipelineMutation, useGetReferenceBookQuery, useGetTVDBChannelsQuery, useGetUserBookQuery, UserBooks } from '../../../services/content_v1';
import { AccountResType, useGetAccountListQuery } from '../../../services/users_v1';
import { useAlert } from '../../alert';
import { extractErrorDetail } from '../../common';
import Button from '../Button/Button';
import Label from '../Label';
import { Modal } from '../Modal';
import Select from '../Select';
import { FeedbackModalProps } from './types';

function toMediaKey(k: string) {
    switch (k.toLowerCase().trim()) {
        case 'название':
        case 'title':
            return 'title';
        case 'description':
        case 'описание':
            return 'description';
        case 'rights_area':
        case 'территория':
        case 'территория прав':
        case 'территория распространения':
            return 'rights_area';
        case 'rights_cat':
        case 'категория прав':
            return 'rights_cat';
        case 'accessibility':
        case 'доступность':
        case 'режим доступности':
            return 'view_access';
        case 'agelimit':
        case 'agerestriction':
        case 'возраст':
        case 'возрастное ограничение':
            return 'agerestriction';
        case 'category':
        case 'категория':
            return 'unit_type';
        case 'country':
        case 'страна':
        case 'страна производства':
            return 'countries';
        case 'language':
        case 'язык':
            return 'languages';
        case 'subs':
        case 'subtitles':
        case 'субтитры':
            return 'subtitles';
        case 'genre':
        case 'жанр':
            return 'genres';
        case 'year':
        case 'год':
        case 'год выпуска':
            return 'startyear';
        case 'quality':
        case 'качество':
            return 'qualities';
        case 'epnum':
        case 'серий':
            return 'epnum';
        case 'timing':
        case 'продолжительность':
        case 'длительность':
        case 'хрономертаж':
            return 'timing';
        default:
            throw Error(`Неизвестный ключ "${k}"`);
            return undefined;
    }
}

function toTVKey(k: string) {
    switch (k.toLowerCase().trim()) {
        case 'название':
        case 'title':
            return 'title';
        case 'description':
        case 'описание':
            return 'description';
        case 'rsterritories':
        case 'территория':
        case 'территория прав':
        case 'территория распространения':
            return 'rsterritories';
        case 'rsmedium':
        case 'среда':
        case 'среда распространения':
            return 'rsmedium';
        case 'accessibility':
        case 'доступность':
        case 'режим доступности':
            return 'view_access';
        case 'agelimit':
        case 'agerestriction':
        case 'возраст':
        case 'возрастное ограничение':
            return 'agerestriction';
        case 'category':
        case 'категория':
            return 'unit_type';
        case 'country':
        case 'countries':
        case 'страна':
        case 'страна производства':
            return 'countries';
        case 'language':
        case 'languages':
        case 'язык':
            return 'languages';
        case 'subs':
        case 'subtitles':
        case 'субтитры':
            return 'subtitles';
        case 'genre':
        case 'genres':
        case 'жанр':
            return 'genres';
        case 'year':
        case 'startyear':
        case 'год':
        case 'год выпуска':
            return 'startyear';
        case 'quality':
        case 'qualities':
        case 'tvqualities':
        case 'качество':
            return 'tvqualities';
        case 'mm':
        case 'тв':
        case 'тв из справочника':
        case 'телеканал':
            return 'mm';
        default:
            throw Error(`Неизвестный ключ "${k}"`);
    }
}

function toAccessType(v: string) {
    switch (v.toLowerCase()) {
        case 'все':
        case 'all':
            return ACCESS_TYPES.ALL;
        case 'vip':
            return ACCESS_TYPES.FAVORITES;
        case 'список':
        case 'list':
            return ACCESS_TYPES.LIST;
        case 'никто':
        case 'nobody':
            return ACCESS_TYPES.NOBODY;
        default:
            return ACCESS_TYPES.ALL;
    }
}

function toRequestData(k: string, val: string, ref: ReferenceBooks, uref: UserBooks, tvdb: TVDBChannel[]) {
    const lowerVal = val.trim().toLowerCase();

    switch (k) {
        case 'title':
            return val.replaceAll(';', ',');
        case 'description':
            return val.replaceAll(';', ',');
        case 'rights_area':
            return lowerVal.split(';').map((v) => uref.rightsarea.find((a) => a.name.toLowerCase() === v.trim())?.id).filter((a) => a !== undefined);
        case 'rights_cat':
            return lowerVal.split(';').map((v) => uref.rightscat.find((a) => a.name.toLowerCase() === v.trim())?.id).filter((a) => a !== undefined);
        case 'view_access':
            return !lowerVal ? ACCESS_TYPES.ALL : toAccessType(lowerVal);
        case 'agerestriction':
            return ref.agerestriction.find((a) => a.displayname.toLowerCase() === lowerVal)?.id;
        case 'unit_type':
            return ref.utype.find((a) => a.displayname.toLowerCase() === lowerVal)?.id;
        case 'countries':
            return lowerVal.split(';').map((v) => ref.country.find((a) => a.displayname.toLowerCase() === v.trim())?.id).filter((a) => a !== undefined);
        case 'languages':
            return lowerVal.split(';').map((v) => ref.language.find((a) => a.displayname.toLowerCase() === v.trim())?.id).filter((a) => a !== undefined);
        case 'subtitles':
            return lowerVal.split(';').map((v) => ref.subtitle.find((a) => a.displayname.toLowerCase() === v.trim())?.id).filter((a) => a !== undefined);
        case 'genres':
            return lowerVal.split(';').map((v) => ref.genre.find((a) => a.displayname.toLowerCase() === v.trim())?.id).filter((a) => a !== undefined);
        case 'startyear':
            return isNaN(Number(lowerVal)) ? undefined : Number(lowerVal);
        case 'qualities':
            return lowerVal.split(';').map((v) => ref.quality.find((a) => a.displayname.toLowerCase() === v.trim())?.id).filter((a) => a !== undefined);
        case 'tvqualities':
            return lowerVal.split(';').map((v) => ref.tvquality.find((a) => a.displayname.toLowerCase() === v.trim())?.id).filter((a) => a !== undefined);
        case 'rsterritories':
            return lowerVal.split(';').map((v) => ref.rsterritory.find((a) => a.displayname.toLowerCase() === v.trim())?.id).filter((a) => a !== undefined);
        case 'rsmedium':
            return lowerVal.split(';').map((v) => ref.rsmedium.find((a) => a.displayname.toLowerCase() === v.trim())?.id).filter((a) => a !== undefined);
        case 'mm':
            return tvdb.find((tv) => tv.brandname.toLowerCase() === lowerVal)?.id;
        case 'epnum':
            return isNaN(Number(lowerVal)) ? undefined : Number(lowerVal);
        case 'timing':
            return isNaN(Number(lowerVal)) ? undefined : Number(lowerVal);
        default:
            return undefined;
    }
}

function toRequestLabels(k: string, val: string, ref: ReferenceBooks, uref: UserBooks, tvdb: TVDBChannel[]) {
    const lowerVal = val.trim().toLowerCase();
    switch (k) {
        case 'title':
            return val.replaceAll(';', ',');
        case 'description':
            return val.replaceAll(';', ',');
        case 'rights_area':
            return lowerVal.split(';').map((v) => uref.rightsarea.find((a) => a.name.toLowerCase() === v.trim())?.name).filter((a) => a !== undefined).join(', ');
        case 'rights_cat':
            return lowerVal.split(';').map((v) => uref.rightscat.find((a) => a.name.toLowerCase() === v.trim())?.name).filter((a) => a !== undefined).join(', ');
        case 'view_access':
            return !lowerVal ? ACCESS_TYPES[ACCESS_TYPES.ALL] : ACCESS_TYPES[toAccessType(lowerVal)];
        case 'agerestriction':
            return ref.agerestriction.find((a) => a.displayname.toLowerCase() === lowerVal)?.displayname;
        case 'unit_type':
            return ref.utype.find((a) => a.displayname.toLowerCase() === lowerVal)?.displayname;
        case 'countries':
            return lowerVal.split(';').map((v) => ref.country.find((a) => a.displayname.toLowerCase() === v.trim())?.displayname).filter((a) => a !== undefined).join(', ');
        case 'languages':
            return lowerVal.split(';').map((v) => ref.language.find((a) => a.displayname.toLowerCase() === v.trim())?.displayname).filter((a) => a !== undefined).join(', ');
        case 'subtitles':
            return lowerVal.split(';').map((v) => ref.subtitle.find((a) => a.displayname.toLowerCase() === v.trim())?.displayname).filter((a) => a !== undefined).join(', ');
        case 'genres':
            return lowerVal.split(';').map((v) => ref.genre.find((a) => a.displayname.toLowerCase() === v.trim())?.displayname).filter((a) => a !== undefined).join(', ');
        case 'startyear':
            return isNaN(Number(lowerVal)) ? undefined : Number(lowerVal);
        case 'qualities':
            return lowerVal.split(';').map((v) => ref.quality.find((a) => a.displayname.toLowerCase() === v.trim())?.displayname).filter((a) => a !== undefined).join(', ');
        case 'tvqualities':
            return lowerVal.split(';').map((v) => ref.tvquality.find((a) => a.displayname.toLowerCase() === v.trim())?.displayname).filter((a) => a !== undefined).join(', ');
        case 'rsterritories':
            return lowerVal.split(';').map((v) => ref.rsterritory.find((a) => a.displayname.toLowerCase() === v.trim())?.displayname).filter((a) => a !== undefined);
        case 'rsmedium':
            return lowerVal.split(';').map((v) => ref.rsmedium.find((a) => a.displayname.toLowerCase() === v.trim())?.displayname).filter((a) => a !== undefined);
        case 'mm':
            return tvdb.find((tv) => tv.brandname.toLowerCase() === lowerVal)?.brandname;
        case 'epnum':
            return isNaN(Number(lowerVal)) ? undefined : Number(lowerVal);
        case 'timing':
            return isNaN(Number(lowerVal)) ? undefined : Number(lowerVal);
        default:
            return undefined;
    }
}

function toHeaderDisplayname(k: string) {
    switch (k) {
        case 'title':
            return 'Название';
        case 'description':
            return 'Описание';
        case 'rights_area':
            return 'Территория прав';
        case 'rights_cat':
            return 'Категория прав';
        case 'view_access':
            return 'Доступность';
        case 'agerestriction':
            return 'Возраст';
        case 'unit_type':
            return 'Категория';
        case 'countries':
            return 'Страна';
        case 'languages':
            return 'Язык';
        case 'subtitles':
            return 'Субтитры';
        case 'genres':
            return 'Жанр';
        case 'startyear':
            return 'Год';
        case 'qualities':
        case 'tvqualities':
            return 'Качество';
        case 'rsterritories':
            return 'Территория';
        case 'rsmedium':
            return 'Среда распространения';
        case 'mm':
            return 'Справочник';
        case 'epnum':
            return 'Серий';
        case 'timing':
            return 'Хронометраж';
        default:
            return undefined;
    }
}

interface ImportContentModalProps {
    file: File | null,
    onClose: () => void;
}

export function ImportMediaContent({ file, onClose }: ImportContentModalProps) {
    const alert = useAlert();

    const [drafts, setDrafts] = useState<any[]>([]);
    const [pures, setPures] = useState<any[]>([]);
    const [labels, setLabels] = useState<any[]>([]);
    const [headers, setHeaders] = useState<string[]>([]);
    const [owner, setOwner] = useState<number | null>(null);

    // const [createRequest] = useCreateContentUnitMutation();
    const [execPipeline] = useExecPipelineMutation();

    const { data: refBooks } = useGetReferenceBookQuery();
    const { data: refUserBooks } = useGetUserBookQuery();

    const { data: accounts } = useGetAccountListQuery({ offset: 0, limit: -1, rtype: AccountResType.PROVIDER });

    // const requestData: ContentRequest = {
    //     title: title.value,
    //     title_eng: titleEng.value,
    //     unit_type: unitType.value,
    //     qualities: qualities,
    //     collections: collections.filter((col) => availCollections.find((ac) => ac.value === col)),
    //     startyear: startyear,
    //     genres: genres.map(gId => Number(gId)),
    //     languages: languages,
    //     subtitles: subtitles,
    //     countries: countries,
    //     description: description,
    //     techinfo: techinfo,
    //     tags: tags.filter((tag) => availTags.find((at) => at.value === tag)),
    //     links: { imdb: imdb.value, kinopoisk: kinopoisk.value },
    //     version: parseInt(version.value),
    //     view_access: viewPermissions,
    //     view_accesslist: userIdViewPermissionList,
    //     download_access: downloadPermissions,
    //     download_accesslist: userIdDownloadPermissionList,
    //     typ: data?.results.typ !== TYPS.CENSORED ? TYPS.NORMAL : undefined,
    //     offers: offerList.map(u => clientMap[u].id || -1),
    //     rights_area: rightAreas2,
    //     rights_cat: rightCats2,
    //     autofill: isModerator(role.label) ? undefined : autoFill,
    //     agerestriction: age,
    //     owner: owner
    // }

    useEffect(() => {
        if (refBooks?.results && refUserBooks?.results) {
            file?.text().then((s) => {
                try {
                    const lines = s.split('\n');
                    const header = lines.at(0);
                    const hs = header?.split(',').map((h) => toMediaKey(h)).filter((h) => h !== undefined) || [];
                    setHeaders(hs);
                    const d: ContentRequest[] = [];
                    const p: ContentRequest[] = [];
                    const ls: string[] = [];
                    lines.forEach((l, i) => {
                        if (i > 0 && l.trim().length) {
                            const draftData: any = {};
                            const requestData: any = {};
                            const labelData: any = {};
                            l.split(',').forEach((v, j) => {
                                const k = hs?.at(j) || '';
                                draftData[k] = v;
                                requestData[k] = toRequestData(k, v, refBooks.results, refUserBooks.results, []);
                                labelData[k] = toRequestLabels(k, v, refBooks.results, refUserBooks.results, []);
                            });
                            draftData.id = i;
                            requestData.id = i;
                            labelData.id = i;
                            d.push(draftData);
                            p.push(requestData);
                            ls.push(labelData);
                        }
                    });
                    setDrafts(d);
                    setPures(p);
                    setLabels(ls);
                } catch (err) {
                    console.log(err);
                    alert.error(extractErrorDetail(err));
                    onClose();
                }
            });
        }

    }, [file, refBooks, refUserBooks]);

    return (
        <div className="form-message _error" style={{ fontSize: '12px' }}>
            <h2 className="popup__heading form-message__heading" style={{ textAlign: 'center' }}>
                Импорт
            </h2>
            <div className="form-message__copy" style={{ textAlign: 'center' }}>
                <Label>Владелец</Label>
                <Select value={owner} onChange={(v) => setOwner(v)} options={accounts?.results.data.map((acc) => ({ value: acc.id, label: acc.name })) || []} />

                {/* <Table className={'catalog-list catalog-list-producer'}>
                    <TableHeader>
                        {headers.map((h) =>
                            <TableHeaderColumn header={toHeaderDisplayname(h) || ''} key={h} field="id" />
                        )}
                    </TableHeader>
                    <TableBody>
                        {drafts.map((d) => <TableRow key={d.id}>
                            {headers.map((h) =>
                                <TableColumn key={h}>{d[h]}</TableColumn>
                            )}
                        </TableRow>
                        )}
                    </TableBody>
                </Table> */}

                {drafts.map((d, i) => <div key={d.id}>
                    <Button size="small" className="_bd-red" style={{ float: 'right' }} onClick={() => {
                        console.log(d.id, drafts)
                        setDrafts([...drafts.filter((z) => z.id !== d.id)]);
                        setLabels([...labels.filter((z) => z.id !== d.id)]);
                        setPures([...pures.filter((z) => z.id !== d.id)]);
                    }}>X</Button>
                    <table width="100%">
                        <thead>
                            <tr>
                                <th>Поле</th>
                                <th>Из файла</th>
                                <th>Будет загружено</th>
                            </tr>
                        </thead>
                        <tbody>
                            {headers.map((h) => <tr key={h}>
                                <td>{toHeaderDisplayname(h)}</td>
                                <td>{d[h]}</td>
                                <td>{labels[i][h]}</td>
                            </tr>)}
                        </tbody>
                    </table>
                    <hr />
                </div>)}
            </div>
            <div className="order__permission-cta">
                <div className="order__permission-cta-item">
                    <Button disabled={!owner || !drafts.length} onClick={() => {
                        execPipeline({
                            pipeline: pures.map((p) => ({
                                method: 'POST',
                                url: '/be/api/v1/content/media/',
                                data: { ...p, owner: owner }
                            }))
                        }).unwrap().then(() => { alert.error(`Импортировано ${pures.length} карточек`); onClose(); }).catch((e) => {
                            const rs = e.data?.response?.results?.filter((r: { response: { code: number }; }) => r.response?.code !== 0);
                            alert.error(`${extractErrorDetail(e)}:\n${extractErrorDetail(rs?.at(0)?.response)}`);
                        })
                        // const tasks = pures.map((p) => createRequest({ ...p, owner: owner }).unwrap());
                        // Promise.all(tasks).then(() => { alert.error(`Импортировано ${tasks.length} карточек`); onClose(); }).catch((e) => alert.error(extractErrorDetail(e)));
                    }}>
                        <Trans>Create</Trans>
                    </Button>
                </div>
                <div className="order__permission-cta-item">
                    <span className="btn _bd" onClick={() => onClose()}>
                        <Trans>Cancel</Trans>
                    </span>
                </div>
            </div>
        </div>
    );
}

export function ImportTVContent({ file, onClose }: ImportContentModalProps) {
    const alert = useAlert();

    const [drafts, setDrafts] = useState<any[]>([]);
    const [pures, setPures] = useState<any[]>([]);
    const [labels, setLabels] = useState<any[]>([]);
    const [headers, setHeaders] = useState<string[]>([]);
    const [owner, setOwner] = useState<number | null>(null);

    // const [createRequest] = useCreateTVUnitMutation();
    const [execPipeline] = useExecPipelineMutation();

    const { data: refBooks } = useGetReferenceBookQuery();
    const { data: refUserBooks } = useGetUserBookQuery();
    const { data: tvdbList } = useGetTVDBChannelsQuery();

    const { data: accounts } = useGetAccountListQuery({ offset: 0, limit: -1, rtype: AccountResType.PROVIDER });

    // const requestData: ContentTVRequest = {
    //     title: title,
    //     title_eng: titleEng.value,
    //     tvqualities: qualities,
    //     collections: collections.filter((col) => availCollections.find((ac) => ac.value === col)),
    //     languages: languages,
    //     subtitles: subtitles,
    //     countries: countries,
    //     description: description,
    //     category: category,
    //     tags: tags.filter((tag) => availTags.find((at) => at.value === tag)),
    //     bcdelivery: bcdelivery,
    //     testperiod: testPeriod,
    //     version: parseInt(version.value),
    //     view_access: viewPermissions,
    //     view_accesslist: userIdViewPermissionList,
    //     typ: data?.results.typ !== TYPS.CENSORED ? TYPS.NORMAL : undefined,
    //     offers: offerList,
    //     autofill: isModerator(role.label) ? undefined : autoFill,
    //     agerestriction: age,
    //     mm: mm,
    //     rsterritories: rsterritories,
    //     rsmedium: rsmedium,
    //     tvbrdcsterr: tvbrdcsterr,
    //     tvdienv: tvdienv,
    //     ssources: [{
    //         sources: sourceSatelliteList,
    //         active: sourceSatellite,
    //         label: SOURCE_TYPES.SATELLITE,
    //         params: null,
    //         sclass: undefined,
    //         stype: SourcesType.SATELLITE,
    //     }, {
    //         sources: sourceLandSources,
    //         active: sourceLand,
    //         label: SOURCE_TYPES.LAND,
    //         params: null,
    //         sclass: undefined,
    //         stype: SourcesType.LAND,
    //     },
    //     {
    //         sources: [],
    //         active: sourceOther,
    //         label: SOURCE_TYPES.OTHER,
    //         params: sourceOtherParams,
    //         sclass: undefined,
    //         stype: SourcesType.OTHER,
    //     }],
    //     owner: owner,
    //     ptype: ptype,
    //     licowner: licOwner,
    //     mmowner: mmOwner,
    //     rowners: rowners,
    //     tvthemes: tvthemes,
    // }

    useEffect(() => {
        if (refBooks?.results && refUserBooks?.results && tvdbList?.results) {
            file?.text().then((s) => {
                try {
                    const lines = s.split('\n');
                    const header = lines.at(0);
                    const hs = header?.split(',').map((h) => toTVKey(h)).filter((h) => h !== undefined) || [];
                    setHeaders(hs);
                    const d: ContentRequest[] = [];
                    const p: ContentRequest[] = [];
                    const ls: string[] = [];
                    lines.forEach((l, i) => {
                        if (i > 0 && l.trim().length) {
                            const draftData: any = {};
                            const requestData: any = {};
                            const labelData: any = {};
                            l.split(',').forEach((v, j) => {
                                const k = hs?.at(j) || '';
                                draftData[k] = v;
                                requestData[k] = toRequestData(k, v, refBooks.results, refUserBooks.results, tvdbList.results);
                                labelData[k] = toRequestLabels(k, v, refBooks.results, refUserBooks.results, tvdbList.results);
                            });
                            draftData.id = i;
                            requestData.id = i;
                            labelData.id = i;
                            d.push(draftData);
                            p.push(requestData);
                            ls.push(labelData);
                        }
                    });
                    setDrafts(d);
                    setPures(p);
                    setLabels(ls);
                } catch (err) {
                    console.log(err);
                    alert.error(extractErrorDetail(err));
                    onClose();
                }
            });
        }

    }, [file, refBooks, refUserBooks, tvdbList]);

    return (
        <div className="form-message _error" style={{ fontSize: '12px' }}>
            <h2 className="popup__heading form-message__heading" style={{ textAlign: 'center' }}>
                Импорт
            </h2>
            <div className="form-message__copy" style={{ textAlign: 'center' }}>
                <Label>Владелец</Label>
                <Select value={owner} onChange={(v) => setOwner(v)} options={accounts?.results.data.map((acc) => ({ value: acc.id, label: acc.name })) || []} />

                {drafts.map((d, i) => <div key={d.id}>
                    <Button size="small" className="_bd-red" style={{ float: 'right' }} onClick={() => {
                        console.log(d.id, drafts)
                        setDrafts([...drafts.filter((z) => z.id !== d.id)]);
                        setLabels([...labels.filter((z) => z.id !== d.id)]);
                        setPures([...pures.filter((z) => z.id !== d.id)]);
                    }}>X</Button>
                    <table width="100%">
                        <thead>
                            <tr>
                                <th>Поле</th>
                                <th>Из файла</th>
                                <th>Будет загружено</th>
                            </tr>
                        </thead>
                        <tbody>
                            {headers.map((h) => <tr key={h}>
                                <td>{toHeaderDisplayname(h)}</td>
                                <td>{d[h]}</td>
                                <td>{labels[i][h]}</td>
                            </tr>)}
                        </tbody>
                    </table>
                    <hr />
                </div>)}
            </div>
            <div className="order__permission-cta">
                <div className="order__permission-cta-item">
                    <Button disabled={!owner || !drafts.length} onClick={() => {
                        execPipeline({
                            pipeline: pures.map((p) => ({
                                method: 'POST',
                                url: '/be/api/v1/content/tv/',
                                data: { ...p, owner: owner }
                            }))
                        }).unwrap().then(() => { alert.error(`Импортировано ${pures.length} карточек`); onClose(); }).catch((e) => {
                            const rs = e.data?.response?.results?.filter((r: { response: { code: number }; }) => r.response?.code !== 0);
                            alert.error(`${extractErrorDetail(e)}:\n${extractErrorDetail(rs?.at(0)?.response)}`);
                        })
                        // const tasks = pures.map((p) => createRequest({ ...p, owner: owner }).unwrap());
                        // Promise.all(tasks).then(() => { alert.error(`Импортировано ${tasks.length} карточек`); onClose(); }).catch((e) => alert.error(extractErrorDetail(e)));
                    }}>
                        <Trans>Create</Trans>
                    </Button>
                </div>
                <div className="order__permission-cta-item">
                    <span className="btn _bd" onClick={() => onClose()}>
                        <Trans>Cancel</Trans>
                    </span>
                </div>
            </div>
        </div>
    );
}

interface ImportContentModalProps extends FeedbackModalProps {
    file: File | null,
}

export function ImportContentModal({ file, isOpen, onClose }: ImportContentModalProps) {
    return (
        <Modal isOpen={isOpen} onClose={() => onClose()} maxWidth={1440}>
            <ImportMediaContent file={file} onClose={onClose} isOpen={false} />
        </Modal >
    );
}

export function ImportTVContentModal({ file, isOpen, onClose }: ImportContentModalProps) {
    return (
        <Modal isOpen={isOpen} onClose={() => onClose()} maxWidth={1440}>
            <ImportTVContent file={file} onClose={onClose} isOpen={false} />
        </Modal >
    );
}